/* UTILS */
import { defineStore } from 'pinia';

import { getFallbackGradients } from '@/helpers/users';
/* TYPES */
import type { Environment, GlobalData, Pusher } from '@/types/config/config.types';

// Reference all state props with url
const authorizedStateURL = [
  'api_url',
  'patrick_url',
  'translation_url',
  'static_assets_base_url',
  'dynamic_assets_base_url',
  'login_url',
  'logout_url',
  'gaston_url',
  'personal_account_url',
  'report_manager_url',
  'mixpanel_proxy',
  'static_data_url'
];

export const useGlobalStore = defineStore('global', {
  state: (): GlobalData => ({
    isAppLoading: true,
    api_url: '',
    patrick_url: '',
    translation_url: '',
    static_assets_base_url: '',
    dynamic_assets_base_url: '',
    login_url: '',
    logout_url: '',
    domainCookies: '',
    gaston_url: '',
    admin_url: '',
    auth_url: '',
    personal_account_url: '',
    report_manager_url: '',
    root_url: '',
    static_data_url: '',
    assessment_url: '',
    mixpanel_proxy: '',
    personality_gradients: [],
    environment: undefined as unknown as Environment,
    cache: false,
    devUsers: [],
    reverb_url: '',
    set_auto_cookie_spaces: false,
    jeudimerci_programme_id: '',
    pusher: undefined as unknown as Pusher,
    activate_beamer: false
  }),
  getters: {
    /**
     * Get the APIs URL
     *
     * @returns {String} The APIs URL
     */
    getAPIURL(state) {
      return state.api_url;
    },

    /**
     * Get the current application loading state
     *
     * @returns {Boolean} True is the app is loading
     */
    isLoading(state) {
      return state.isAppLoading;
    },
    /**
     * Get a global URL
     *
     * @returns {String} - global URL. False if item is not authorized
     */
    getURL: state => item => {
      if (authorizedStateURL.indexOf(item) !== 1) {
        return state[item];
      }

      return false;
    },
    getStaticDataUrl(state): string {
      return `${state.static_data_url}static-data/`;
    },
    /**
     * Get current environment (dev, preprod, ....)
     */
    getEnvironment(state): Environment {
      return state.environment;
    },
    /**
     * Return query's cache enabling status
     */
    getHasCache(state) {
      return state.cache;
    },
    /**
     * Return pusher config
     */
    getPusher(state) {
      return state.pusher;
    },
    getReverbUrl(state) {
      return state.reverb_url;
    },
    /**
     * Return mixpanel proxy
     */
    getMixpanelProxy(state) {
      return state.mixpanel_proxy;
    },
    /**
     * Get users filled with the config json file
     */
    getDevUsers(state) {
      return state.devUsers;
    },
    /**
     * Get if we need to set cookies spaces for local dev with the config json file
     */
    getAutoCookieSpaces(state) {
      return state.set_auto_cookie_spaces;
    },
    /**
     * Get personality gradients
     */
    getPersonalityGradients: state => (disc_id?: string) => {
      if (disc_id && state.personality_gradients[disc_id]) {
        return state.personality_gradients[disc_id].gradients ?? getFallbackGradients();
      }
      return getFallbackGradients();
    }
  },
  actions: {
    setLoading(value) {
      this.isAppLoading = value;
    },
    setAPIURL(url) {
      this.api_url = url;
    },
    setPatrickURL(url) {
      this.patrick_url = url;
    },
    setTranslationURL(url) {
      this.translation_url = url;
    },
    setStaticAssetsURL(url) {
      this.static_assets_base_url = url;
    },
    setAssetsURL(url) {
      this.dynamic_assets_base_url = url;
    },
    setLogoutURL(url) {
      this.logout_url = url;
    },
    setDomainCookies(domain) {
      this.domainCookies = domain;
    },
    setGastonUrl(url) {
      this.gaston_url = url;
    },
    setAdminUrl(url) {
      this.admin_url = url;
    },
    setAuthUrl(url) {
      this.auth_url = url;
    },
    setPersonalAccountUrl(url) {
      this.personal_account_url = url;
    },
    setRootUrl(url) {
      this.root_url = url;
    },
    setAssessmentURL(url) {
      this.assessment_url = url;
    },
    setEnvironment(environment: Environment) {
      this.environment = environment;
    },
    setCache(cacheConfig) {
      this.cache = cacheConfig;
    },
    setPusher(pusherData) {
      this.pusher = pusherData;
    },
    setReverbUrl(url) {
      this.reverb_url = url;
    },
    setMixpanelProxy(proxyUrl) {
      this.mixpanel_proxy = proxyUrl;
    },
    setDevUsers(devUsers) {
      this.devUsers = devUsers;
    },
    setReportManagerUrl(report_manager_url) {
      this.report_manager_url = report_manager_url;
    },
    setAutoCookieSpaces(set_auto_cookie_spaces) {
      this.set_auto_cookie_spaces = set_auto_cookie_spaces;
    },
    setStaticDataUrl(url) {
      this.static_data_url = url;
    },
    setPersonalityGradients(gradients) {
      this.personality_gradients = gradients;
    },
    setJeuDiMerciProgrammeId(programme_id) {
      this.jeudimerci_programme_id = programme_id;
    },
    setActivateBeamer(activateBeamer) {
      this.activate_beamer = activateBeamer === true || activateBeamer === 'true';
    }
  }
});
